// Field (job page round): a labelled text box, text area or select, 38 tall (DESIGN.md §8's field). Its
// label sits above it, muted; what it's for under it; a problem with what was typed under that, in ink
// words after StateMark's red bar, with a red line round the box, since it needs the person now (§2; red
// is a mark, never small text). React Aria ties the label, the help and the error
// to the box for screen readers. A select is the browser's own, so it works the same on every device.
// Reads only --input-*.
import { useId, type ReactNode } from 'react';
import {
  FieldError,
  Input,
  Label,
  Text,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
} from 'react-aria-components';
import { StateMark } from '../StateMark/StateMark.tsx';
import './Field.css';

interface Common {
  label: string;
  /** What it's for, under it ("Letters and numbers, as on their paperwork"). */
  description?: ReactNode;
  /** What's wrong with it, in plain words; under it, after a red bar. */
  error?: string | null;
  /** Says "required" beside the label; the form checks it. */
  isRequired?: boolean;
  isDisabled?: boolean;
  name?: string;
  /** Keeps the label for screen readers only, where the box says what it is (a search, a message). */
  hideLabel?: boolean;
  /** Marked wrong (the red line) while the words saying why are elsewhere, by `describedBy`'s id: a
   * refused booking names its field, and says why under the button that booked it (QA-720). */
  isInvalid?: boolean;
  /** The id of words elsewhere that describe the box (the refusal under Book). */
  describedBy?: string;
}

function LabelText({ label, isRequired }: { label: string; isRequired?: boolean }) {
  return (
    <>
      {label}
      {isRequired ? <span className="fl-input__req"> · required</span> : null}
    </>
  );
}

export interface TextFieldProps extends Common {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** A text area for more than a line (a reason, a message). */
  multiline?: boolean;
  autoFocus?: boolean;
  /** A time or a date is the browser's own picker (Time off's From, To, Starts and Ends); a number keeps
   * its arrows and min and max. */
  type?: 'text' | 'email' | 'tel' | 'search' | 'date' | 'time' | 'number';
  /** A number's least and most; a date's or time's earliest and latest (Time off's To is never before its
   * From). */
  min?: number | string;
  max?: number | string;
  maxLength?: number;
  /**
   * A box as wide as what it holds, beside its words in a row: a number (right-aligned), a time, a short
   * code (a document number's prefix) or a day (a list's days from and to, beside its search).
   */
  width?: 'number' | 'time' | 'short' | 'date';
  /** After the box, muted, on its line: what the number counts ("hours a day"). */
  unit?: string;
  /** Leaving the box (a check that runs once a field is typed, as a new customer's duplicate check). */
  onBlur?: () => void;
  /** The keyboard a phone or tablet shows: decimal for an amount (truck stock: how many, in feet). */
  inputMode?: 'text' | 'decimal' | 'numeric' | 'search';
  /** Shown but not changed here (a booking from an estimate keeps its customer). */
  isReadOnly?: boolean;
  /** The box, for a screen that puts the focus back in it (a form closed beside it). */
  inputRef?: { current: HTMLInputElement | null };
  /** The box offers what's typed matches in a list under it (Book's customer): a combobox, `controls`
   * the list's id, `expanded` while it shows. */
  combobox?: { expanded: boolean; controls: string };
  /** The browser's own suggestions: off where the screen offers its own. */
  autoComplete?: 'off';
}

export function TextField({
  label,
  description,
  error,
  isRequired,
  isDisabled,
  name,
  value,
  onChange,
  placeholder,
  multiline,
  autoFocus,
  type = 'text',
  hideLabel,
  onBlur,
  inputMode,
  min,
  max,
  width,
  unit,
  maxLength,
  isInvalid,
  describedBy,
  isReadOnly,
  inputRef,
  combobox,
  autoComplete,
}: TextFieldProps) {
  const box = multiline ? (
    <AriaTextArea className="fl-input__box" data-multiline="" placeholder={placeholder} />
  ) : (
    <Input
      ref={inputRef}
      className="fl-input__box"
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete={autoComplete}
      {...(combobox
        ? { role: 'combobox', 'aria-expanded': combobox.expanded, 'aria-controls': combobox.controls }
        : {})}
      {...(min !== undefined ? { min } : {})}
      {...(max !== undefined ? { max } : {})}
    />
  );
  return (
    <AriaTextField
      className="fl-input"
      data-width={width}
      value={value}
      onChange={onChange}
      isInvalid={!!error || !!isInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      name={name}
      type={type}
      {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      autoFocus={autoFocus}
      {...(maxLength != null ? { maxLength } : {})}
      validationBehavior="aria"
      {...(onBlur ? { onBlur: () => onBlur() } : {})}
    >
      <Label className="fl-input__label" data-hidden={hideLabel ? '' : undefined}>
        <LabelText label={label} isRequired={isRequired} />
      </Label>
      {unit ? (
        <span className="fl-input__with">
          {box}
          <span className="fl-input__unit">{unit}</span>
        </span>
      ) : (
        box
      )}
      {description ? (
        <Text slot="description" className="fl-input__help">
          {description}
        </Text>
      ) : null}
      <FieldError className="fl-input__error">
        {error != null ? (
          <>
            <StateMark state="problem" />
            {error}
          </>
        ) : undefined}
      </FieldError>
    </AriaTextField>
  );
}

/** One choice in a SelectField; `isDisabled` keeps it in the list but not choosable (a window already started). */
export interface SelectOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

export interface SelectFieldProps extends Common {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** A box as wide as its longest choice, so the parts after it share its line (the receptionist's hours:
   * the choice, then the times and the days). */
  width?: 'fit';
}

/**
 * A choice from a list, the browser's own select. An option that can't be chosen now stays in the list,
 * greyed by the browser and skipped by the keys, its label saying why ("8–10 a.m. (already started)").
 */
export function SelectField({
  label,
  description,
  error,
  isRequired,
  isDisabled,
  name,
  value,
  onChange,
  options,
  hideLabel,
  isInvalid,
  describedBy,
  width,
}: SelectFieldProps) {
  const id = useId();
  const help = description ? `${id}-help` : undefined;
  const err = error ? `${id}-error` : undefined;
  return (
    <div className="fl-input" data-width={width}>
      <label className="fl-input__label" htmlFor={id} data-hidden={hideLabel ? '' : undefined}>
        <LabelText label={label} isRequired={isRequired} />
      </label>
      <select
        id={id}
        className="fl-input__box"
        data-select=""
        name={name}
        value={value}
        disabled={isDisabled}
        required={isRequired}
        aria-invalid={error || isInvalid ? true : undefined}
        aria-describedby={[help, err, describedBy].filter(Boolean).join(' ') || undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.isDisabled || undefined}>
            {o.label}
          </option>
        ))}
      </select>
      {description ? (
        <span id={help} className="fl-input__help">
          {description}
        </span>
      ) : null}
      {error ? (
        <span id={err} className="fl-input__error">
          <StateMark state="problem" />
          {error}
        </span>
      ) : null}
    </div>
  );
}
